import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';

import '../MainFeed/FeedLayout.scss';
import { LOOKBOOK_PARAMETERS_TO_COPY } from '@pages/Root/pages/Lookbook/contants.ts';
import { cleanUpAds, initAds } from '../../utils/ads-util.ts';
import { initPrebidAnalytics } from '../../utils/ad-prebid-analytics.ts';
import { adImpressionCollector } from '../../utils/ad-impression-collector.ts';
import { isDntEnabled } from '@utils/dnt.ts';
import {
  logAdImpressionMetric,
  logAdNotRenderedMetric,
  logAdRenderedMetric,
  logAdRequestedMetric,
} from '@/tracing/events.ts';
import { useBridge } from '../../bridge/bridge-provider.tsx';
import { AdConfigContext } from '@moment/context/AdContext.ts';
import { useAdStackAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';

export interface AdInitLayoutProps extends PropsWithChildren {
  brandGroupId: string;
  utmSource: string;
}

export const GamAdsInitLayout: FC<AdInitLayoutProps> = ({
  utmSource,
  children,
}) => {
  const [searchParams] = useSearchParams();
  const bridge = useBridge();
  const adStackAnalytics = useAdStackAnalytics();
  const context = useContext(CommonPageDataContext);

  const [isDoNotSell] = useState<boolean>(isDntEnabled());
  const [hideAdSlotIds, setHideAdSlotIds] = useState<string[]>([]);
  const slotRequestCounts = useRef<Map<string, number>>(new Map());
  const slotTargetingAtRequest = useRef<Map<string, { hbPb: string; hbBidder: string }>>(new Map());

  const handleSlotRequested = (event: any) => {
    console.log(
      `GAM ad event 'slotRequested' received: ${JSON.stringify(event)}`
    );
    const slotId = event?.slot?.getSlotElementId();
    const adUnitPath = event?.slot?.getAdUnitPath();
    logAdRequestedMetric({
      ad_unit_id: slotId,
      ad_tag: adUnitPath || '',
      utm_source: utmSource,
    });
    const count = (slotRequestCounts.current.get(slotId) ?? 0) + 1;
    slotRequestCounts.current.set(slotId, count);
    if (count > 1) {
      adStackAnalytics.emitSlotDuplicateRefreshEvent({
        adUnitCode: slotId,
        adUnitPath: adUnitPath || '',
        refreshCount: count,
      });
    }
    const hbPb = event?.slot?.getTargeting('hb_pb')?.[0] ?? '';
    const hbBidder = event?.slot?.getTargeting('hb_bidder')?.[0] ?? '';
    slotTargetingAtRequest.current.set(slotId, { hbPb, hbBidder });
  };

  const handleSlotOnload = (event: any) => {
    const slotId = event?.slot?.getSlotElementId();
    const adUnitPath = event?.slot?.getAdUnitPath();
    adStackAnalytics.emitSlotLoadedEvent({
      adUnitCode: slotId,
      adUnitPath: adUnitPath || '',
    });
  };

  const handleSlotResponseReceived = (event: any) => {
    const slotId = event?.slot?.getSlotElementId();
    const adUnitPath = event?.slot?.getAdUnitPath();
    adStackAnalytics.emitSlotResponseReceivedEvent({
      adUnitCode: slotId,
      adUnitPath: adUnitPath || '',
    });
  };

  const handleAdImpression = (event: any) => {
    console.log(
      `GAM ad event 'impressionViewable' received: ${JSON.stringify(event)}`
    );
    const slotId = event?.slot?.getSlotElementId();
    const adUnitPath = event?.slot?.getAdUnitPath();
    logAdImpressionMetric({
      ad_unit_id: slotId,
      ad_tag: adUnitPath || '',
      utm_source: utmSource,
    });
    adImpressionCollector.emitAdImpression(bridge, slotId, 'AdInitLayout');
    adImpressionCollector.emitAdMonetizationCPM(
      bridge,
      adUnitPath || '',
      'AdInitLayout'
    );
    adStackAnalytics.emitViewableImpressionEvent({
      partnerId: context.partner || '',
      adTag: adUnitPath,
      adUnitId: slotId,
      searchParams,
    });
  };

  const handleSlotRenderEnded = (event: any) => {
    console.log(
      `GAM ad event 'slotRenderEnded' received: ${JSON.stringify(event)}`
    );
    const slotId = event?.slot?.getSlotElementId();
    const adUnitPath = event?.slot?.getAdUnitPath();
    if (event.isEmpty) {
      const adHidden = hideAdSlotIds?.includes(slotId);
      if (!adHidden) {
        const newHiddenSlots = hideAdSlotIds.concat(slotId);
        setHideAdSlotIds(newHiddenSlots);
        adStackAnalytics.emitAdNotRenderedEvent({
          partnerId: context.partner || '',
          adTag: adUnitPath,
          adUnitId: slotId,
          searchParams,
        });
        logAdNotRenderedMetric({
          ad_unit_id: slotId,
          ad_tag: adUnitPath || '',
          utm_source: utmSource,
        });
      }
    } else {
      logAdRenderedMetric({
        ad_unit_id: slotId,
        ad_tag: adUnitPath || '',
        utm_source: utmSource,
      });
      adStackAnalytics.emitImpressionEvent({
        partnerId: context.partner || '',
        adTag: adUnitPath,
        adUnitId: slotId,
        searchParams,
      });
      const savedTargeting = slotTargetingAtRequest.current.get(slotId);
      adStackAnalytics.emitSlotRenderedEvent({
        adUnitCode: slotId,
        adUnitPath: adUnitPath || '',
        lineItemId: event.lineItemId ?? null,
        advertiserId: event.advertiserId ?? null,
        hbPb: savedTargeting?.hbPb ?? '',
        hbBidder: savedTargeting?.hbBidder ?? '',
      });
      slotTargetingAtRequest.current.delete(slotId);
    }
  };

  const adEventHandler = (eventName: string, event: any) => {
    console.log(
      `GAM ad event '${eventName}' received: ${JSON.stringify(event)}`
    );
  };

  const initAdParams = useCallback(() => {
    const parameterList = [];
    for (const parameter of LOOKBOOK_PARAMETERS_TO_COPY) {
      const parameterValue = searchParams.get(parameter === 'mp_app_version' ? 'app_version' : parameter);
      if (parameterValue) {
        parameterList.push({ parameter, parameterValue });
      }
    }
    initAds(parameterList, [
      { eventType: 'impressionViewable', eventListener: handleAdImpression },
      {
        eventType: 'gameManualInterstitialSlotClosed',
        eventListener: (event) => {
          adEventHandler('gameManualInterstitialSlotClosed', event);
        },
      },
      {
        eventType: 'gameManualInterstitialSlotReady',
        eventListener: (event) => {
          adEventHandler('gameManualInterstitialSlotReady', event);
        },
      },
      {
        eventType: 'rewardedSlotClosed',
        eventListener: (event) => {
          adEventHandler('rewardedSlotClosed', event);
        },
      },
      {
        eventType: 'rewardedSlotGranted',
        eventListener: (event) => {
          adEventHandler('rewardedSlotGranted', event);
        },
      },
      {
        eventType: 'rewardedSlotReady',
        eventListener: (event) => {
          adEventHandler('rewardedSlotReady', event);
        },
      },
      { eventType: 'slotOnload', eventListener: handleSlotOnload },
      { eventType: 'slotRenderEnded', eventListener: handleSlotRenderEnded },
      { eventType: 'slotRequested', eventListener: handleSlotRequested },
      { eventType: 'slotResponseReceived', eventListener: handleSlotResponseReceived },
      {
        eventType: 'slotVisibilityChanged',
        eventListener: (event) => {
          adEventHandler('slotVisibilityChanged', event);
        },
      },
    ]);
  }, [searchParams]);

  useEffect(() => {
    initPrebidAnalytics(adStackAnalytics);
  }, []);

  useEffect(() => {
    if (!isDoNotSell) {
      initAdParams();
    }

    return () => {
      cleanUpAds();
    };
  }, [initAdParams, cleanUpAds]);

  const adContextValue = useMemo(
    () => ({
      hideAdSlotIds,
    }),
    [hideAdSlotIds]
  );

  return (
    <AdConfigContext.Provider value={adContextValue}>
      <div id="ad--init">{children}</div>
    </AdConfigContext.Provider>
  );
};
